/**
 * Ranking by several criteria at once, computed in the browser.
 *
 * WHY IT MOVED OFF THE SERVER
 * ---------------------------
 * This was the most expensive endpoint the site had and the most fragile. It
 * scored up to twelve thousand candidates across every selected criterion, and
 * it is what exceeded the Worker CPU limit — Cloudflare returned its own error
 * page, and the app rendered "Nothing to rank" for a query with ten thousand
 * eligible products.
 *
 * The inputs are already on the device. Selecting four criteria means the app
 * has four baked rankings sitting in the service worker cache, and combining
 * them is a Borda count over a few hundred rows: microseconds of work in the
 * browser, versus a database query, a Worker invocation and a CPU ceiling.
 *
 * WHAT IT COMBINES, EXACTLY
 * -------------------------
 * Products that appear in the top of AT LEAST ONE selected ranking. That is a
 * real and stateable rule, and the UI states it. It is not the same set the
 * server considered, and it is not presented as if it were — a product that is
 * mediocre at every selected criterion, and therefore in the top of none, is
 * not in this ranking.
 *
 * WHY BORDA
 * ---------
 * The metrics have incompatible units: grams per dollar, dollars per hundred
 * calories, grams per hundred grams. Averaging raw values is meaningless.
 * Averaging POSITIONS is not — it asks "how well does this do across the things
 * you chose", which is the actual question.
 */

import { computeMetrics, METRIC_DIRECTION, type MetricId } from '@/lib/nutrition/calculations';
import type { Product } from '@/types/product';

/** Which metric orders each board. Mirrors the server's board definitions. */
export const BOARD_METRIC: Record<string, MetricId> = {
  cheapest_per_100g: 'pricePer100g',
  cheapest_per_100ml: 'pricePer100ml',
  cheapest_outright: 'price',
  largest_package: 'packageSize',
  protein_per_dollar: 'proteinPerDollar',
  cheapest_per_100kcal: 'pricePer100kcal',
  protein_per_100kcal: 'proteinPer100kcal',
  carbs_per_100kcal: 'carbsPer100kcal',
  fat_per_100kcal: 'fatPer100kcal',
  highest_protein: 'protein100',
  highest_carbs: 'carb100',
  highest_fat: 'fat100',
};

export interface CombinedEntry {
  productId: string;
  /** Mean position across the criteria this product qualifies for. */
  score: number;
  /** How many of the selected criteria could rank it. */
  criteriaMet: number;
  criteriaTotal: number;
  /**
   * Where it placed on each criterion it qualified for.
   *
   * Carried so the client-side result is the same shape the server produced —
   * a narrower version would quietly become a second contract for the UI to
   * handle.
   */
  perBoard: Array<{ boardId: string; rank: number; of: number; percentile: number }>;
}

export interface CombinedResult {
  products: Product[];
  entries: CombinedEntry[];
  /** How many distinct products were considered. */
  eligible: number;
}

/**
 * Combine several boards' products into one ranking.
 *
 * `byBoard` maps a board id to the products baked for it, already in that
 * board's own order.
 */
export function combineBoards(
  byBoard: Map<string, Product[]>,
  boardIds: readonly string[],
  limit: number,
): CombinedResult {
  const selected = boardIds.filter((id) => byBoard.has(id) && BOARD_METRIC[id] !== undefined);
  if (selected.length === 0) return { products: [], entries: [], eligible: 0 };

  // Every product any selected board offers, de-duplicated.
  const pool = new Map<string, Product>();
  for (const id of selected) {
    for (const product of byBoard.get(id) ?? []) {
      if (!pool.has(product.id)) pool.set(product.id, product);
    }
  }
  if (pool.size === 0) return { products: [], entries: [], eligible: 0 };

  /*
   * Positions are recomputed over the WHOLE pool, not read off each board.
   *
   * A product ranked #3 of the sixty baked for one board may sit differently
   * once the other boards' products join the comparison. Using its original
   * position would score it against a set that is no longer the set being
   * ranked, and the combination would reward whichever board happened to be
   * shortest.
   */
  const metrics = new Map<string, ReturnType<typeof computeMetrics>>();
  for (const [id, product] of pool) metrics.set(id, computeMetrics(product));

  const positions = new Map<string, Array<{ boardId: string; rank: number; of: number }>>();
  for (const boardId of selected) {
    const metric = BOARD_METRIC[boardId]!;
    const higher = METRIC_DIRECTION[metric] === 'higher';

    const ranked: Array<{ id: string; value: number }> = [];
    for (const [id] of pool) {
      const value = metrics.get(id)?.values[metric]?.value ?? null;
      if (value === null || !Number.isFinite(value)) continue;
      ranked.push({ id, value });
    }
    ranked.sort((a, b) => (higher ? b.value - a.value : a.value - b.value));

    ranked.forEach((entry, i) => {
      const list = positions.get(entry.id) ?? [];
      list.push({ boardId, rank: i + 1, of: ranked.length });
      positions.set(entry.id, list);
    });
  }

  const entries: CombinedEntry[] = [];
  for (const [id, ranks] of positions) {
    if (ranks.length === 0) continue;
    /*
     * Averaged, then penalised for the criteria it could not meet.
     *
     * A product with nutrition data ranked by four criteria should not be
     * beaten by one that has a price and nothing else and therefore holds a
     * single very good position. The penalty pushes partial qualifiers down
     * without excluding them, which matters while only 12.6% of the catalogue
     * has nutrition at all.
     */
    const mean = ranks.reduce((a, b) => a + b.rank, 0) / ranks.length;
    const missing = selected.length - ranks.length;
    entries.push({
      productId: id,
      score: mean + missing * pool.size,
      criteriaMet: ranks.length,
      criteriaTotal: selected.length,
      perBoard: ranks.map((r) => ({
        ...r,
        percentile: r.of > 0 ? (r.rank / r.of) * 100 : 100,
      })),
    });
  }

  entries.sort((a, b) => a.score - b.score);
  const top = entries.slice(0, limit);

  return {
    products: top
      .map((e) => pool.get(e.productId))
      .filter((p): p is Product => p !== undefined),
    entries: top,
    eligible: entries.length,
  };
}

/** True when every selected board has baked products available. */
export function canCombineLocally(
  byBoard: Map<string, Product[]>,
  boardIds: readonly string[],
): boolean {
  if (boardIds.length < 2) return false;
  return boardIds.every((id) => (byBoard.get(id)?.length ?? 0) > 0);
}
